import type { FormInstance } from 'antd';
import type { Moment } from 'moment';

import { DAYS_OF_WEEK } from '@src/constants/commons';
import { translateString } from '@src/util/intl/translateString';

import { DEFAULT_ACTIVE_DAY } from '../../constants';
import type { ActiveDayValue } from '../../types';

interface DayWithSchedule {
  key: string | number;
  short: string;
  schedule: ActiveDayValue;
}

interface DayGroup {
  startDay: DayWithSchedule;
  endDay: DayWithSchedule;
  schedule: ActiveDayValue;
}

// Compare if two schedules are equal (start and end times)
export const schedulesEqual = (
  d1: ActiveDayValue | null | undefined,
  d2: ActiveDayValue | null | undefined,
): boolean => {
  if (!d1 || !d2) return false;
  return (d1.startTime as Moment).isSame(d2.startTime as Moment) && (d1.endTime as Moment).isSame(d2.endTime as Moment);
};

// Group consecutive days with the same schedule
export const groupDays = (daysArr: DayWithSchedule[]): DayGroup[] => {
  const groups: DayGroup[] = [];

  if (!Array.isArray(daysArr) || daysArr.length === 0) return groups;

  let currentGroup: DayGroup = {
    startDay: daysArr[0],
    endDay: daysArr[0],
    schedule: daysArr[0].schedule,
  };

  for (let i = 1; i < daysArr.length; i += 1) {
    const currentDay = daysArr[i];
    const isConsecutive =
      parseInt(String(currentDay.key), 10) === parseInt(String(currentGroup.endDay.key), 10) + 1;
    const hasSameSchedule = schedulesEqual(currentDay.schedule, currentGroup.schedule);

    if (isConsecutive && hasSameSchedule) {
      currentGroup.endDay = currentDay;
    } else {
      groups.push(currentGroup);
      currentGroup = {
        startDay: currentDay,
        endDay: currentDay,
        schedule: currentDay.schedule,
      };
    }
  }

  groups.push(currentGroup);
  return groups;
};

// Format regulation name with grouped days
export const formatRegulationName = (days: Array<string | number>, form: FormInstance): string => {
  const daysData = DAYS_OF_WEEK.filter(day => days.includes(day.value));
  try {
    const sortedDays = [...daysData].sort((a, b) => a.order - b.order);

    // Create array of active days with their data
    const activeDaysInfo = sortedDays
      .map(day => {
        const dayData = (form.getFieldValue(`day_${day.value}`) || DEFAULT_ACTIVE_DAY) as ActiveDayValue;

        const dayInfo = DAYS_OF_WEEK.find(d => d.value === day.value);

        // Skip if day data is not valid
        if (!dayData || !dayData.isActive || !dayData.startTime || !dayData.endTime) {
          return null;
        }

        return {
          key: day.order,
          short: dayInfo?.short || day.label,
          schedule: dayData,
        };
      })
      .filter(Boolean) as DayWithSchedule[];

    // Group consecutive days with same schedule
    const groups = groupDays(activeDaysInfo);

    // Format each group
    const formattedGroups = groups.map(group => {
      const startShort = group.startDay.short;
      const endShort = group.endDay.short;
      const startTime = (group.schedule.startTime as Moment).format('HH:mm');
      const endTime = (group.schedule.endTime as Moment).format('HH:mm');
      const timeRange = `${startTime}-${endTime}`;

      if (startShort === endShort) {
        return `${startShort} ${timeRange}`;
      }
      return `${startShort} ${translateString('general.to')} ${endShort} ${timeRange}`;
    });

    return formattedGroups.join(', ');
  } catch (error) {
    // Error is handled by returning an empty string
    return '';
  }
};

export const calculateTotalHours = (
  formDays: Array<string | number> | null | undefined,
  form: FormInstance,
): number => {
  if (!formDays || !Array.isArray(formDays)) return 0;

  return formDays.reduce<number>((acc, day) => {
    try {
      const dayData = form.getFieldValue(`day_${day}`) as ActiveDayValue | undefined;
      if (!dayData?.isActive || !dayData?.startTime || !dayData?.endTime) {
        return acc;
      }

      const { startTime, endTime } = dayData;
      const breakTime = dayData.breakTime || 0;

      // Calcular la diferencia en minutos y convertir a horas decimales
      const minutes = (endTime as Moment).diff(startTime as Moment, 'minutes');
      const hours = (minutes - breakTime) / 60;

      return acc + Math.max(0, hours); // Asegurar que no sea negativo
    } catch (error) {
      // Error is handled by returning the accumulated value
      return acc;
    }
  }, 0);
};

export const buildDynamicFooter = (formDays: Array<string | number>, form: FormInstance): string => {
  const totalHours = calculateTotalHours(formDays, form);
  return totalHours.toFixed(2);
};
